import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { HitoBadge } from "../components/HitoBadge";

/** Calendario regulatorio: el "reloj" del market access español — próximos
 *  hitos (OPR y su ventana de recurso, trimestres de precios menores, CIPM,
 *  SNS, nomenclátor) + referencia de plazos tasados (RD 415/2026). */

type CalEvent = {
  key: string;
  title: string;
  description: string;
  date: string | null;
  window: { start: string; end: string } | null;
  cadence: string;
  official: boolean;
  status: "open" | "upcoming" | "estimated" | "closed" | "past" | "info";
  days_left: number | null;
};
type RefCard = { key: string; title: string; items: string[]; note: string };
// Ventanas de publicación por fuente (R-32 / C-07, §90 Fase 4D)
type Publication = {
  source_code: string; source_name: string; label: string | null;
  next_start: string | null; next_end: string | null; open_now: boolean;
  last_start: string | null; last_end: string | null; last_published: boolean | null;
};
type Payload = { today: string; events: CalEvent[]; reference: RefCard[]; publications?: Publication[] };

// Hitos futuros (R-22, §90 Fase 3): el mismo objeto que un hito ocurrido, con
// fecha estimada y estado previsto / cumplido / vencido.
/* Rediseño 9 sep (Luis): el panel se organiza por lo que el usuario HACE, no
   por el estado interno de la predicción.
     cuenta_atras · fecha ya determinada que ninguna fuente confirma (fin de
                    exclusividad). Es un calendario: no se «incumple».
     sin_llegar   · hito confirmable pasado de plazo. Eso sí es noticia.
     cumplido     · llegó dentro de lo esperado.
   Y por encima, el filtro que de verdad ordena a una persona de acceso: si el
   producto es suyo (defender precio) o de un competidor (anticipar entrada). */
type Bucket = "cuenta_atras" | "sin_llegar" | "cumplido";
type Titularidad = "" | "propio" | "competencia";

type Prediction = {
  id: string; status: "previsto" | "cumplido" | "vencido"; kind: "cuenta_atras" | "prediccion";
  product_name: string | null; active_ingredient: string | null; anchor_code: string | null;
  from_hito_name: string | null; hito_id: string; hito_name: string | null; hito_group: number | null;
  severity: "A" | "D" | "I" | null;
  origin_at: string; expected_at: string; days_left: number; days_since_origin: number;
  fulfilled_at: string | null;
  plazo: { days: number | null; basis: "legal" | "empirico"; label: string | null; norma: string | null } | null;
  is_portfolio: boolean; is_competitor: boolean;
  // Protección (PE-02/PE-03, 28 sep): cuenta atrás calculada sobre el registro de la Unión
  detalle?: string | null; sujeto_confirmacion?: boolean; eu_number?: string | null;
};
type PredPage = { total: number; items: Prediction[]; counts: Record<Bucket, number> };

const BUCKET_COLOR: Record<Bucket, string> = { cuenta_atras: "#3b82f6", sin_llegar: "#ef4444", cumplido: "#10b981" };

function PredictionsPanel({ panel }: { panel: React.CSSProperties }) {
  const t = useT();
  const f = useFmt();
  const [bucket, setBucket] = useState<Bucket>("cuenta_atras");
  const [quien, setQuien] = useState<Titularidad>("");
  // Ventana de la cuenta atrás de protección: por defecto 36 meses (Carlos, 28 sep).
  const [horizon, setHorizon] = useState<number>(36);
  const [data, setData] = useState<PredPage | null>(null);
  useEffect(() => {
    const p = new URLSearchParams({ status: bucket, limit: "50", horizon_months: String(horizon) });
    if (quien) p.set("titularidad", quien);
    api.get<PredPage>(`/hitos/previstos?${p}`).then(setData).catch(() => setData(null));
  }, [bucket, quien, horizon]);
  const counts = data?.counts ?? { cuenta_atras: 0, sin_llegar: 0, cumplido: 0 };
  const hint = bucket === "cuenta_atras" ? "calendar.predCountdownHint"
    : bucket === "sin_llegar" ? "calendar.predLateHint" : "calendar.predDoneHint";

  const tab: React.CSSProperties = { border: 0, cursor: "pointer", padding: "6px 12px", fontSize: "0.75rem", fontWeight: 600 };
  return (
    <div style={{ ...panel, padding: "1.1rem 1.25rem", marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{t("calendar.predictionsTitle")}</h2>
        <div style={{ marginLeft: "auto", display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden" }}>
          {(["cuenta_atras", "sin_llegar", "cumplido"] as const).map(b => (
            <button key={b} onClick={() => setBucket(b)} style={{
              ...tab,
              background: bucket === b ? BUCKET_COLOR[b] : "transparent",
              color: bucket === b ? "#fff" : "var(--clr-text3)",
            }}>
              {t(`calendar.pred_${b}`)} {counts[b] > 0 && `(${counts[b]})`}
            </button>
          ))}
        </div>
      </div>

      {/* Mío o de la competencia: son dos trabajos distintos (defender el precio
          frente a anticipar una entrada), así que se filtran por separado. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>{t(hint)}</span>
        {bucket === "cuenta_atras" && (
          <label style={{ marginLeft: "auto", fontSize: "0.72rem", color: "var(--clr-text3)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {t("calendar.predHorizon")}
            <select value={horizon} onChange={e => setHorizon(Number(e.target.value))}
              style={{ fontSize: "0.72rem", padding: "3px 6px", borderRadius: 8, border: "1px solid var(--clr-border)", background: "var(--clr-surface)", color: "var(--clr-text)" }}>
              {[12, 24, 36, 60, 120].map(m => <option key={m} value={m}>{t("calendar.predHorizonMonths", { n: m })}</option>)}
            </select>
          </label>
        )}
        <div style={{ marginLeft: bucket === "cuenta_atras" ? 0 : "auto", display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden" }}>
          {([["", "calendar.predAll"], ["propio", "calendar.predOwn"], ["competencia", "calendar.predRival"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setQuien(k as Titularidad)} style={{
              ...tab, fontSize: "0.72rem",
              background: quien === k ? "var(--clr-org-primary)" : "transparent",
              color: quien === k ? "#fff" : "var(--clr-text3)",
            }}>{t(label)}</button>
          ))}
        </div>
      </div>

      {data && data.items.length === 0 && (
        <div style={{ color: "var(--clr-text4)", fontSize: "0.82rem", padding: 12 }}>{t("calendar.predictionsEmpty")}</div>
      )}
      {data?.items.map(p => (
        <div key={p.id} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "0.7rem 0", borderTop: "1px solid var(--clr-border2)" }}>
          <DateBlock iso={p.expected_at.slice(0, 10)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>{p.product_name || p.active_ingredient || p.anchor_code}</span>
              <HitoBadge c={{ hito_id: p.hito_id, hito_name: p.hito_name, hito_group: p.hito_group, severity: p.severity }} t={t} maxWidth={300} showSeverity={false} />
              {p.is_portfolio && <Capsule text={t("calendar.predPortfolio")} color="#06b6d4" dot />}
              {p.is_competitor && <Capsule text={t("calendar.predCompetitor")} color="#f59e0b" dot />}
              {bucket === "cuenta_atras" && p.days_left >= 0 && (
                <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                  {p.days_left === 0 ? t("calendar.today") : t(p.days_left === 1 ? "calendar.inDay" : "calendar.inDays", { n: p.days_left })}
                </span>
              )}
              {/* Comparación en vez de binario: «lo normal son X, este lleva Y»
                  dice mucho más que la etiqueta «vencido». */}
              {bucket === "sin_llegar" && (
                <Capsule color="#ef4444" dot
                  text={p.plazo?.days
                    ? t("calendar.predUsual", { n: p.plazo.days, m: p.days_since_origin })
                    : t("calendar.predUsualUnknown", { m: p.days_since_origin })} />
              )}
              {bucket === "cumplido" && p.fulfilled_at && <Capsule text={t("calendar.predDoneOn", { date: f.date(p.fulfilled_at, { day: "2-digit", month: "2-digit", year: "numeric" }) })} color="#10b981" />}
            </div>
            <div style={{ fontSize: "0.76rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>
              {t("calendar.predOrigin", { hito: p.from_hito_name ?? "", date: f.date(p.origin_at, { day: "2-digit", month: "2-digit", year: "numeric" }) })}
              {p.plazo && p.plazo.days != null && (
                <span title={p.plazo.norma ?? undefined} style={{ marginLeft: 6, color: "var(--clr-text4)" }}>
                  · {t(p.plazo.basis === "legal" ? "calendar.predBasisLegal" : "calendar.predBasisEmpirical", { n: p.plazo.days })}
                </span>
              )}
              {p.eu_number && <span style={{ marginLeft: 6, color: "var(--clr-text4)", fontFamily: "ui-monospace, monospace" }}>· {p.eu_number}</span>}
            </div>
            {p.detalle && <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: 2, lineHeight: 1.45 }}>{p.detalle}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
  backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
  boxShadow: "var(--clr-shadow-sm)",
};

function Capsule({ text, color, dot }: { text: string; color: string; dot?: boolean }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, background: color + "1e",
      color, borderRadius: 999, padding: "3px 11px", fontSize: "0.72rem", fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 3, background: color, flexShrink: 0 }} />}
      {text}
    </span>
  );
}

function DateBlock({ iso }: { iso: string }) {
  const t = useT();
  const meses = t("calendar.monthsShort").split(",");
  const d = new Date(iso + "T00:00:00");
  return (
    <div style={{ width: 56, flexShrink: 0, textAlign: "center", padding: "6px 0", border: "1px solid var(--clr-border2)", borderRadius: 12, background: "var(--clr-surface)" }}>
      <div style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{d.getDate()}</div>
      <div style={{ fontSize: "0.66rem", color: "var(--clr-text3)", textTransform: "uppercase", letterSpacing: 0.8 }}>
        {meses[d.getMonth()]} {d.getFullYear() !== new Date().getFullYear() ? d.getFullYear() : ""}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const t = useT();
  const f = useFmt();
  const { isMobile } = useBreakpoint();
  const fmt = (iso: string) =>
    f.date(iso + "T00:00:00", { day: "2-digit", month: "2-digit", year: "numeric" });
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get<Payload>(`/calendar?lang=${f.lang}`).then(setData).catch(e => setErr(e.message));
  }, [f.lang]);

  const recurso = data?.events.find(e => e.key === "opr_recurso" && e.status === "open");
  const upcoming = data?.events.filter(e => e.status !== "closed" && e.status !== "past") ?? [];
  const closed = data?.events.filter(e => e.status === "closed" || e.status === "past") ?? [];

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("calendar.title")}</h1>
      <p style={{ margin: "4px 0 18px", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
        {t("calendar.subtitle")}
      </p>
      {err && <div style={{ color: "#f87171", fontSize: "0.82rem", marginBottom: 12 }}>{t("calendar.errorLabel")} {err}</div>}

      {/* ── Ventana de recurso OPR abierta: banner destacado ── */}
      {recurso && (
        <div style={{ ...panel, border: "1px solid #ef444455", background: "#ef44440d", padding: "1rem 1.25rem", marginBottom: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: "-0.01em", marginBottom: 4 }}>⚖️ {recurso.title}</div>
            <div style={{ fontSize: "0.8rem", color: "var(--clr-text2)", lineHeight: 1.5 }}>{recurso.description}</div>
          </div>
          <div style={{ textAlign: "center", padding: "0.6rem 1.2rem", borderRadius: 14, background: "#ef44441e" }}>
            <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "#ef4444", letterSpacing: "-0.02em", lineHeight: 1 }}>{recurso.days_left}</div>
            <div style={{ fontSize: "0.66rem", color: "#ef4444", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginTop: 4 }}>{t("calendar.daysDeadline")}</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 360px", gap: 16, alignItems: "start" }}>
        {/* ── Próximos hitos + hitos previstos (§90 Fase 3) ── */}
        <div>
        <div style={{ ...panel, padding: "1.1rem 1.25rem" }}>
          <h2 style={{ margin: "0 0 12px", fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{t("calendar.upcomingMilestones")}</h2>
          {!data && !err && <div style={{ color: "var(--clr-text4)", fontSize: "0.82rem", padding: 12 }}>{t("calendar.loading")}</div>}
          {upcoming.map(e => (
            <div key={e.key} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "0.8rem 0", borderTop: "1px solid var(--clr-border2)" }}>
              {(e.date || e.window) && <DateBlock iso={e.status === "open" && e.window ? e.window.end : (e.date ?? e.window!.end)} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>{e.title}</span>
                  <Capsule text={e.cadence} color="#3b82f6" />
                  {e.official
                    ? <Capsule text={t("calendar.official")} color="#10b981" />
                    : <Capsule text={t("calendar.estimated")} color="#f59e0b" />}
                  {e.status === "open" && <Capsule text={t("calendar.deadlineOpen")} color="#ef4444" dot />}
                  {e.days_left !== null && e.days_left >= 0 && e.status !== "open" && (
                    <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                      {e.days_left === 0
                        ? t("calendar.today")
                        : t(e.days_left === 1 ? "calendar.inDay" : "calendar.inDays", { n: e.days_left })}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>{e.description}</div>
                {e.window && (
                  <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: 4 }}>
                    {t("calendar.windowLabel")} {fmt(e.window.start)} → {fmt(e.window.end)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {closed.length > 0 && (
            <>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", margin: "14px 0 4px" }}>{t("calendar.recent")}</div>
              {closed.map(e => (
                <div key={e.key} style={{ padding: "0.5rem 0", borderTop: "1px solid var(--clr-border2)", opacity: 0.7 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>{e.title}</span>
                    <Capsule text={t("calendar.closed")} color="#94a3b8" />
                  </div>
                  {e.window && (
                    <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", marginTop: 2 }}>
                      {fmt(e.window.start)} → {fmt(e.window.end)}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
        <PredictionsPanel panel={panel} />
        </div>

        {/* ── Referencia normativa + próximas publicaciones ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {data?.publications && data.publications.length > 0 && (
            <div style={{ ...panel, padding: "1.1rem 1.25rem" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: "0.88rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{t("calendar.pubTitle")}</h3>
              <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginBottom: 8 }}>{t("calendar.pubHint")}</div>
              {data.publications.map(p => (
                <div key={p.source_code} style={{ padding: "6px 0", borderTop: "1px solid var(--clr-border2)", fontSize: "0.78rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600 }}>{p.source_name}</span>
                    {p.open_now && <Capsule text={t("calendar.pubOpen")} color="#3b82f6" dot />}
                    {p.last_published === false && <Capsule text={t("calendar.pubMissing")} color="#ef4444" dot />}
                    {p.last_published === true && <Capsule text={t("calendar.pubOk")} color="#10b981" />}
                  </div>
                  <div style={{ color: "var(--clr-text4)", fontSize: "0.72rem", marginTop: 2 }}>
                    {p.next_start && p.next_end && t("calendar.pubNext", { from: fmt(p.next_start), to: fmt(p.next_end) })}
                    {p.last_published === false && p.last_end && ` · ${t("calendar.pubMissingSince", { date: fmt(p.last_end) })}`}
                  </div>
                </div>
              ))}
            </div>
          )}
          {data?.reference.map(r => (
            <div key={r.key} style={{ ...panel, padding: "1.1rem 1.25rem" }}>
              <h3 style={{ margin: "0 0 10px", fontSize: "0.88rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{r.title}</h3>
              <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
                {r.items.map((it, i) => (
                  <li key={i} style={{ fontSize: "0.8rem", color: "var(--clr-text2)", lineHeight: 1.7 }}>{it}</li>
                ))}
              </ul>
              <div style={{ fontSize: "0.72rem", color: "var(--clr-text4)", lineHeight: 1.5 }}>{r.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
